import {
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useMemo,
} from 'react';
import { useIntl } from 'react-intl';
import {
  StripeProvider,
  CheckoutForm,
  convertLegacyStripeStyle,
  type LegacyStripeStyle,
} from '@anyroad/stripe';
import {
  useCreatePaymentIntent,
  useAttachCustomerToPaymentIntent,
} from './paymentIntentsApi';

interface PaymentProps {
  bookingRequestId: string;
  totalPriceCents: number;
  currency?: string;
  stripeTheme?: LegacyStripeStyle;
  onIntentConfirm: () => void;
  defaultValues?: {
    name?: string | null;
    email?: string | null;
  };
  onIntentCreated?: (paymentIntentId?: string) => void;
}

export interface PaymentHandle {
  submit: () => void;
}

/**
 * Modern Stripe payment integration using refactored @anyroad/stripe components.
 * Demonstrates pattern from scs/checkout/frontend following mobile_first conventions.
 * Integrates with payment intent API endpoints using react-query hooks.
 *
 * @example Usage from parent component:
 * ```tsx
 * const paymentRef = useRef<PaymentHandle>(null);
 *
 * const handleSubmit = () => {
 *   if (paymentRef.current) {
 *     paymentRef.current.submit(); // This triggers the Stripe form submission
 *   }
 * };
 *
 * return (
 *   <>
 *     <Payment ref={paymentRef} {...props} />
 *     <button onClick={handleSubmit}>Submit Payment</button>
 *   </>
 * );
 * ```
 */
export const Payment = forwardRef<PaymentHandle, PaymentProps>(
  (
    {
      bookingRequestId,
      totalPriceCents,
      currency,
      stripeTheme,
      defaultValues,
      onIntentConfirm,
      onIntentCreated,
    },
    ref
  ) => {
    const { formatMessage } = useIntl();
    const checkoutFormRef = useRef<HTMLFormElement>(null);

    const appearance = useMemo(() => {
      const converted = stripeTheme
        ? convertLegacyStripeStyle(stripeTheme)
        : undefined;
      return converted;
    }, [stripeTheme]);

    useEffect(() => {
      if (stripeTheme?.fonts && stripeTheme.fonts.length > 0) {
        stripeTheme.fonts.forEach((font) => {
          if (font.cssSrc) {
            const existingLink = document.querySelector(
              `link[href="${font.cssSrc}"]`
            );
            if (!existingLink) {
              const link = document.createElement('link');
              link.rel = 'stylesheet';
              link.href = font.cssSrc;
              document.head.appendChild(link);
            }
          }
        });
      }
    }, [stripeTheme]);

    // Create payment intent mutation
    const {
      mutate: createPaymentIntent,
      data: paymentIntentData,
      isPending,
      error,
    } = useCreatePaymentIntent();

    // Attach customer to payment intent mutation
    const { mutateAsync: attachCustomer } = useAttachCustomerToPaymentIntent();

    useImperativeHandle(
      ref,
      () => ({
        submit: () => {
          if (checkoutFormRef.current) {
            checkoutFormRef.current.requestSubmit();
          }
        },
      }),
      []
    );

    useEffect(() => {
      if (bookingRequestId && totalPriceCents > 0) {
        createPaymentIntent({
          bookingRequestId,
          data: {
            amount_cents: totalPriceCents,
            currency: currency || 'usd',
          },
        });
      }
    }, [bookingRequestId, totalPriceCents, currency]);

    useEffect(() => {
      if (paymentIntentData?.payment_intent_id && onIntentCreated) {
        onIntentCreated(paymentIntentData.payment_intent_id);
      }
    }, [paymentIntentData?.payment_intent_id, onIntentCreated]);

    const localizationStrings = {
      errorMessage: formatMessage({
        defaultMessage: 'An error occurred during payment. Please try again.',
        description: 'Error message displayed when a payment error occurs',
      }),
      processingMessage: formatMessage({
        defaultMessage: 'Processing',
        description: 'Message displayed while payment is being processed',
      }),
      requiresPaymentMethodMessage: formatMessage({
        defaultMessage: 'Please enter your payment details.',
        description: 'Message displayed when payment details are required',
      }),
      submitButtonText: formatMessage({
        defaultMessage: 'Confirm Purchase',
        description: 'Text for the submit button on the payment form',
      }),
      successMessage: formatMessage({
        defaultMessage: 'Payment successful',
        description: 'Message displayed when payment is successful',
      }),
    };

    if (isPending) {
      return (
        <div>
          {formatMessage({
            defaultMessage: 'Processing',
            description: 'Message displayed while payment is being processed',
          })}
        </div>
      );
    }

    if (error || !paymentIntentData || !paymentIntentData.client_secret) {
      return (
        <div>
          {error?.message ||
            formatMessage({
              defaultMessage:
                'An error occurred during payment. Please try again.',
              description:
                'Error message displayed when a payment error occurs',
            })}
        </div>
      );
    }

    return (
      <StripeProvider
        appearance={appearance}
        clientSecret={paymentIntentData.client_secret}
        connectedAccountId={paymentIntentData.managed_account_token || null}
        publishableKey={process.env.STRIPE_PUBLISHABLE_KEY || ''}
      >
        <CheckoutForm
          ref={checkoutFormRef}
          dataTestId="mobile-first-payment"
          hideButton
          localizationStrings={localizationStrings}
          defaultValues={{
            name: defaultValues?.name || '',
            email: defaultValues?.email || '',
          }}
          onBeforeConfirm={
            paymentIntentData?.payment_intent_id &&
            (defaultValues?.email || defaultValues?.name)
              ? async () => {
                  await attachCustomer({
                    bookingRequestId,
                    paymentIntentId: paymentIntentData.payment_intent_id,
                    data: {
                      email: defaultValues?.email || undefined,
                      name: defaultValues?.name || undefined,
                    },
                  });
                }
              : undefined
          }
          onIntentConfirm={onIntentConfirm}
        />
      </StripeProvider>
    );
  }
);

Payment.displayName = 'Payment';
